// Community moderation as the dashboard sees it (community wave 6). Moderators: the whole queue and every action.
// A game's creator: the queue and log of their own game (`?game=` required; reporters stay private), and only hide,
// unhide and dismiss there. Creators can dismiss reports on a review but not hide it (`reviews_need_moderator`: they
// can't bury reviews of their own game), and can't unhide what a moderator hid (`hidden_by_moderator`).

export type Scope = "moderator" | "creator";
export type ModAction = "hide" | "unhide" | "dismiss" | "remove" | "ban_author" | "resolve_burst";

export interface Report {
  id: string;
  reason: string;
  note?: string | null;
  /** moderators only: the creator-scoped queue leaves it out */
  reporter_id?: string;
  created_at: number;
}

export interface QueueItem {
  target_type: string;
  target_id: string;
  game_id: string;
  target_author_id?: string | null;
  preview?: string;
  state?: string;
  reports: Report[];
}

export interface Queue {
  scope: Scope;
  items: QueueItem[];
}

export interface LogEntry {
  id: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  game_id: string | null;
  /** community wave 6; older entries have none */
  actor_role?: "moderator" | "creator" | "service" | null;
  created_at: number;
}

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const str = (v: unknown): string => (typeof v === "string" ? v : "");

/**
 * The queue answer. Before wave 6 only moderators got an answer at all, so a missing `scope` means moderator.
 */
export function parseQueue(raw: unknown): Queue {
  const r = obj(raw);
  const items: QueueItem[] = (Array.isArray(r.items) ? r.items : []).map(obj).flatMap((it) => {
    if (!str(it.target_type) || !str(it.target_id)) return [];
    return [
      {
        target_type: str(it.target_type),
        target_id: str(it.target_id),
        game_id: str(it.game_id),
        target_author_id: typeof it.target_author_id === "string" ? it.target_author_id : null,
        preview: str(it.preview),
        state: str(it.state),
        reports: (Array.isArray(it.reports) ? it.reports : []).map(obj).map((x) => ({
          id: str(x.id),
          reason: str(x.reason) || "other",
          note: typeof x.note === "string" ? x.note : null,
          ...(typeof x.reporter_id === "string" ? { reporter_id: x.reporter_id } : {}),
          created_at: typeof x.created_at === "number" ? x.created_at : 0,
        })),
      },
    ];
  });
  return { scope: r.scope === "creator" ? "creator" : "moderator", items };
}

export function parseLog(raw: unknown): LogEntry[] {
  const r = obj(raw);
  return (Array.isArray(r.log) ? r.log : Array.isArray(r.entries) ? r.entries : []).map(obj).map((e) => ({
    id: str(e.id),
    action: str(e.action),
    target_type: typeof e.target_type === "string" ? e.target_type : null,
    target_id: typeof e.target_id === "string" ? e.target_id : null,
    game_id: typeof e.game_id === "string" ? e.game_id : null,
    actor_role: e.actor_role === "moderator" || e.actor_role === "creator" || e.actor_role === "service" ? e.actor_role : null,
    created_at: typeof e.created_at === "number" ? e.created_at : 0,
  }));
}

/** What this viewer may do to a target (the buttons offered; community stays the authority). */
export function allowed(scope: Scope, targetType: string): ModAction[] {
  if (scope === "moderator") return targetType === "review_burst" ? ["resolve_burst"] : ["hide", "unhide", "dismiss", "remove"];
  if (targetType === "review_burst") return [];
  if (targetType === "review") return ["dismiss"];
  return ["hide", "unhide", "dismiss"];
}

export interface Hidden {
  target_type: string;
  target_id: string;
  game_id?: string | null;
  /** who hid it: a creator can't unhide a moderator's hide */
  by: "moderator" | "creator" | "service" | null;
  at: number;
}

/**
 * Targets whose latest hide/unhide/remove in the log (newest first) is a hide, with who hid them. `gameId` null: every
 * game in the log (the moderators' site-wide view).
 */
export function hiddenTargets(log: LogEntry[], gameId: string | null, targetType?: string): Hidden[] {
  const seen = new Set<string>();
  const out: Hidden[] = [];
  for (const e of log) {
    if (!e.target_type || !e.target_id || (gameId !== null && e.game_id !== gameId)) continue;
    if (targetType && e.target_type !== targetType) continue;
    const key = `${e.target_type}/${e.target_id}`;
    if (seen.has(key)) continue;
    if (e.action === "hide" || e.action === "unhide" || e.action === "remove") {
      seen.add(key);
      if (e.action === "hide") out.push({ target_type: e.target_type, target_id: e.target_id, game_id: e.game_id, by: e.actor_role ?? null, at: e.created_at });
    }
  }
  return out;
}

/** Can this viewer unhide it? A creator can't undo a moderator's (or the service's) hide. */
export function canUnhide(scope: Scope, h: Pick<Hidden, "by" | "target_type">): boolean {
  if (scope === "moderator") return true;
  return h.target_type !== "review" && (h.by === "creator" || h.by === null);
}

/** Plain words for community's moderation refusals. */
export function modError(code: string): string | null {
  const m: Record<string, string> = {
    reviews_need_moderator: "Only Banger moderators can hide or unhide a review, so creators can't bury reviews of their own game. You can dismiss reports on it.",
    hidden_by_moderator: "A Banger moderator hid this, so only a moderator can bring it back.",
    not_moderator: "Only Banger moderators can do that.",
    not_found: "That's gone already. Reload to see the latest.",
  };
  return m[code] ?? null;
}

export const ACTION_DONE: Record<ModAction, string> = {
  hide: "Hidden",
  unhide: "Visible again",
  dismiss: "Reports dismissed",
  remove: "Removed",
  ban_author: "Author banned",
  resolve_burst: "Review wave resolved",
};

/** A report reason in words ("off_topic" → "Off topic", "review_bombing" → "Review bombing"). */
export function reasonLabel(reason: string): string {
  const s = reason.replace(/_/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Other";
}

// ---- titles and the site-wide queue (web wave 8) ----------------------------------------------------------------

export const targetKey = (type: string, id: string): string => `${type}/${id}`;

/** A target type in words, for badges and the type filter. */
export const TYPE_LABEL: Record<string, string> = {
  review: "Review",
  thread: "Thread",
  post: "Reply",
  bug: "Bug report",
  clip: "Clip",
  review_burst: "Review wave",
};
export const typeLabel = (t: string): string => TYPE_LABEL[t] ?? reasonLabel(t);

/** What a list shows when nothing better is known: never a bare id. */
export function titleFallback(type: string): string {
  const m: Record<string, string> = { review: "A review", thread: "A thread", post: "A reply in a thread", bug: "A bug report", clip: "An untitled clip", review_burst: "A wave of reviews" };
  return m[type] ?? "An item on the game's page";
}

/** One line of text, cut at a word near `max` characters. */
export function shortTitle(text: string, max = 120): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const sp = cut.lastIndexOf(" ");
  return (sp > max * 0.6 ? cut.slice(0, sp) : cut).replace(/[\s.,;:!?-]+$/, "") + "…";
}

/** Titles from community's queue answer (`preview` is a target's title, or its body when it has none). */
export function titlesFromQueue(items: QueueItem[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const it of items) if (it.preview && it.preview.trim()) out.set(targetKey(it.target_type, it.target_id), shortTitle(it.preview));
  return out;
}

/** The title in a single-item read: `GET /threads/:id` → thread, `/bugs/:id` → bug, `/clips/:id` → clip. */
export function titleFromRead(type: string, raw: unknown): string | null {
  const r = obj(raw);
  const item = obj(type === "thread" ? r.thread : type === "bug" ? r.bug : type === "clip" ? r.clip : null);
  const t = str(item.title) || str(item.body);
  return t.trim() ? shortTitle(t) : null;
}

/** The types with a single-item read a moderator or the game's creator can open even while hidden. */
export const READABLE_TYPES = new Set(["thread", "bug", "clip"]);

export type AgeFilter = "any" | "day" | "week" | "over_week" | "over_month";
export const AGE_FILTERS: [AgeFilter, string][] = [
  ["any", "Any age"],
  ["day", "Reported in the last 24 hours"],
  ["week", "Reported in the last 7 days"],
  ["over_week", "Waiting over 7 days"],
  ["over_month", "Waiting over 30 days"],
];

export interface QueueFilter {
  game: string | null;
  type: string | null;
  age: AgeFilter;
}

export function queueFilterFromQuery(q: URLSearchParams): QueueFilter {
  const game = q.get("game");
  const type = q.get("type");
  const age = q.get("age") as AgeFilter | null;
  return {
    game: game && /^[A-Za-z0-9_-]{1,64}$/.test(game) ? game : null,
    type: type && type in TYPE_LABEL ? type : null,
    age: age && AGE_FILTERS.some(([a]) => a === age) ? age : "any",
  };
}

export function queueFilterToQuery(f: QueueFilter): string {
  const p = new URLSearchParams();
  if (f.game) p.set("game", f.game);
  if (f.type) p.set("type", f.type);
  if (f.age !== "any") p.set("age", f.age);
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** When the item's first open report came in (how long it has waited), unix seconds; 0 when unknown. */
export function waitingSince(it: QueueItem): number {
  const ts = it.reports.map((r) => r.created_at).filter((t) => t > 0);
  return ts.length ? Math.min(...ts) : 0;
}

/** The newest report on the item, unix seconds. */
export function lastReported(it: QueueItem): number {
  return it.reports.reduce((m, r) => Math.max(m, r.created_at), 0);
}

/**
 * The site-wide queue's filters. Age: "in the last N" looks at the newest report (fresh activity), "waiting over N"
 * at the oldest open one (how long it has gone unanswered).
 */
export function filterQueue(items: QueueItem[], f: QueueFilter, now = Date.now() / 1000): QueueItem[] {
  const DAY = 86400;
  return items.filter((it) => {
    if (f.game && it.game_id !== f.game) return false;
    if (f.type && it.target_type !== f.type) return false;
    switch (f.age) {
      case "day":
        return now - lastReported(it) <= DAY;
      case "week":
        return now - lastReported(it) <= 7 * DAY;
      case "over_week":
        return waitingSince(it) > 0 && now - waitingSince(it) > 7 * DAY;
      case "over_month":
        return waitingSince(it) > 0 && now - waitingSince(it) > 30 * DAY;
      default:
        return true;
    }
  });
}
